import { useState, useEffect, useRef, useCallback } from "react";
import {
  RewardsHubServicesApi,
  Barcode,
  BarcodeResult,
  SevenElevenWarning,
  isSevenElevenWarning,
} from "../../common/api/RewardsHubServicesApi";
import { GLOBAL_CONST } from "../../constants";
import { useExpiryTimer } from "./useExpiryTimer";
import { EventLogger } from "../../common/utils/EventLogger";
import { delay, redirectCloseFullscreen } from "../../utils";
import axios from "axios";

export type BarcodeStatus = "loading" | "error" | "quotaUsed" | "warning" | "success";

export interface BarcodeError {
  errorMsg: string;
  debugInfo: string;
}

export const useBarcode = (isActive: boolean | null, driverLevel?: string) => {
  const [status, setStatus] = useState<BarcodeStatus>("loading");
  const [barcode, setBarcode] = useState<Barcode | null>(null);
  const [warning, setWarning] = useState<SevenElevenWarning | null>(null);
  const [lastError, setLastError] = useState<BarcodeError | null>(null);
  const isLoadingRef = useRef(false);
  const apiRef = useRef<RewardsHubServicesApi | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1500;

  apiRef.current ??= new RewardsHubServicesApi();

  const updateBarcodeStatus = useCallback((response: Barcode) => {
    if (response?.barcodeNumber) {
      setStatus("success");
      setLastError(null);
      setWarning(null);
      retryCountRef.current = 0;
    } else if (response?.quotaRemaining === 0) {
      setStatus("quotaUsed");
      setLastError({
        errorMsg: "Barcode quota used",
        debugInfo: `getBarcode+200+quotaRemaining=0&quotaLimit=${response?.quotaLimit ?? 0}`,
      });
    } else {
      setStatus("error");
      setLastError({
        errorMsg: "Invalid barcode data",
        debugInfo: "getBarcode+200+noBarcodeNumber",
      });
    }
  }, []);

  const canProceedWithRequest = useCallback(() => {
    return !isLoadingRef.current;
  }, []);

  const shouldUpdateState = useCallback((controller: AbortController) => {
    return !controller.signal.aborted && isMountedRef.current;
  }, []);

  const isRequestCanceled = useCallback((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    return (
      message.toLowerCase().includes("cancel") ||
      message.toLowerCase().includes("abort") ||
      message.toLowerCase().includes("stream cancelled")
    );
  }, []);

  const scheduleRetry = useCallback(() => {
    isLoadingRef.current = false;
    setTimeout(() => {
      if (isMountedRef.current && isActive && fetchBarcodeRef.current) {
        fetchBarcodeRef.current();
      }
    }, RETRY_DELAY);
  }, [isActive]);

  const handleSuccessResponse = useCallback(
    async (response: BarcodeResult, controller: AbortController) => {
      if (!shouldUpdateState(controller)) return;

      if (isSevenElevenWarning(response)) {
        try {
          await EventLogger.logInfoEvent("7-Eleven barcode warning received", {
            code: response.code,
            message: response.message,
          });
        } catch (logError) {
          console.warn("Error logging barcode warning:", logError);
        }
        if (shouldUpdateState(controller)) {
          retryCountRef.current = 0;
          setBarcode(null);
          setWarning(response);
          setLastError(null);
          setStatus("warning");
        }
        return;
      }

      try {
        if (response?.barcodeNumber) {
          await EventLogger.logInfoEvent("Barcode fetched successfully", {
            driverId: response.driverId,
            hasBarcode: !!response.barcodeNumber,
          });
          await delay(50);
          if (shouldUpdateState(controller)) {
            setBarcode(response);
            updateBarcodeStatus(response);
          }
        } else {
          throw new Error("Invalid barcode data received");
        }
      } catch (logError) {
        console.warn("Error logging barcode success:", logError);
        if (shouldUpdateState(controller)) {
          setBarcode(response);
          updateBarcodeStatus(response);
        }
      }
    },
    // eslint-disable-next-line
    [updateBarcodeStatus],
  );

  const handleBarcodeError = useCallback(
    async (error: unknown, controller: AbortController) => {
      if (!shouldUpdateState(controller)) {
        return;
      }

      // Check for 403 (JWT token expired) error
      // This issue commonly happens on iOS as there is an issue with auto refresh of the token
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        console.warn("JWT token expired (403), triggering error callback");
        try {
          await EventLogger.logErrorEvent("JWT token expired", {
            status: error.response.status,
            message: "Access token expired",
          });
        } catch (logError) {
          console.warn("Error logging JWT expiration:", logError);
        }

        if (shouldUpdateState(controller)) {
          setStatus("error");
          setLastError({
            errorMsg: "Session expired",
            debugInfo: "getBarcode+403+jwtExpired",
          });
        }

        // this function contains backward compatibility for the old native app
        redirectCloseFullscreen();
        return;
      }

      if (isRequestCanceled(error)) {
        apiRef.current = new RewardsHubServicesApi();
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          isLoadingRef.current = false;
          setTimeout(() => {
            if (isMountedRef.current && isActive && fetchBarcodeRef.current) {
              fetchBarcodeRef.current();
            }
          }, 200);
          return;
        }
        if (shouldUpdateState(controller)) {
          setStatus("error");
          setLastError({
            errorMsg: "Request failed",
            debugInfo: "getBarcode+cancelled+maxRetries",
          });
        }
        return;
      }
      try {
        await EventLogger.logErrorEvent("Failed to fetch barcode", error);
        await delay(50);
      } catch (logError) {
        console.warn("Error logging barcode fetch failure:", logError);
      }
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        scheduleRetry();
        return;
      }
      console.error("Max retry attempts reached for barcode fetch");
      if (shouldUpdateState(controller)) {
        const axiosErr = axios.isAxiosError(error) ? error : null;
        const statusCode = axiosErr?.response?.status || "unknown";
        const errMsg = axiosErr?.response?.data?.message || axiosErr?.message || "Unknown error";
        setStatus("error");
        setLastError({
          errorMsg: errMsg,
          debugInfo: `getBarcode+${statusCode}+maxRetries`,
        });
      }
    },
    // eslint-disable-next-line
    [isRequestCanceled, scheduleRetry, isActive],
  );

  const fetchBarcode = useCallback(
    async (isManualRefresh = false) => {
      if (!canProceedWithRequest()) {
        return;
      }
      if (!isActive) {
        return;
      }
      if (abortControllerRef.current && !isManualRefresh) {
        abortControllerRef.current.abort();
        await delay(50);
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      if (isManualRefresh || retryCountRef.current > 0) {
        apiRef.current = new RewardsHubServicesApi();
      }
      isLoadingRef.current = true;
      setStatus("loading");
      try {
        await EventLogger.logInfoEvent("Fetching 7-Eleven barcode", {
          accountId: GLOBAL_CONST.DATA.accountId,
          attempt: retryCountRef.current + 1,
          isManualRefresh,
        });
        await delay(isManualRefresh ? 50 : 100);
        const response = await apiRef.current!.getBarcode({
          accountId: GLOBAL_CONST.DATA.accountId,
          driverLevel,
        });
        await handleSuccessResponse(response, controller);
      } catch (error) {
        await handleBarcodeError(error, controller);
      } finally {
        isLoadingRef.current = false;
      }
      // eslint-disable-next-line
    },
    // eslint-disable-next-line
    [isActive, handleSuccessResponse, handleBarcodeError, canProceedWithRequest, shouldUpdateState, driverLevel],
  );

  const fetchBarcodeRef = useRef<(() => Promise<void>) | null>(fetchBarcode);
  fetchBarcodeRef.current = fetchBarcode;

  const handleExpiry = useCallback(async () => {
    await delay(200);
    if (isMountedRef.current && isActive && !isLoadingRef.current && fetchBarcodeRef.current) {
      await fetchBarcodeRef.current();
    }
  }, [isActive]);

  useExpiryTimer(barcode?.notOnOrAfter ?? null, handleExpiry);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        if (isMountedRef.current && fetchBarcodeRef.current) {
          fetchBarcodeRef.current();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch (error) {
          console.warn("Error cleaning up abort controller:", error);
        }
      }
      isLoadingRef.current = false;
      isMountedRef.current = false;
    };
  }, []);

  return { status, barcode, warning, lastError, fetchBarcode };
};

export default useBarcode;
